import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '../lib/cn';
import { isImpersonating } from '../lib/impersonation';

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  action?: ToastAction;
  /** Pass `Infinity` for a sticky toast (no auto-dismiss). Defaults to 5000 ms. */
  duration?: number;
  /** Stable id — re-using the same id replaces the existing toast instead of stacking duplicates. */
  id?: string;
}

export interface Toast {
  id: string;
  kind: ToastKind;
  message: string;
  action?: ToastAction;
  duration: number;
}

type Listener = (toast: Toast) => void;

const listeners: Listener[] = [];

export function showToast(kind: ToastKind, message: string, options?: ToastOptions): void {
  const toast: Toast = {
    id: options?.id ?? `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    kind,
    message,
    action: options?.action,
    duration: options?.duration ?? 5000,
  };
  for (const l of listeners) l(toast);
}

export const toast = {
  success: (message: string, options?: ToastOptions) => showToast('success', message, options),
  error: (message: string, options?: ToastOptions) => showToast('error', message, options),
  info: (message: string, options?: ToastOptions) => showToast('info', message, options),
};

export function Toaster() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const add: Listener = (t) => {
      setToasts((prev) => {
        const without = prev.filter((x) => x.id !== t.id);
        return [...without, t];
      });
      if (Number.isFinite(t.duration)) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((x) => x.id !== t.id));
        }, t.duration);
      }
    };
    listeners.push(add);
    return () => {
      const i = listeners.indexOf(add);
      if (i >= 0) listeners.splice(i, 1);
    };
  }, []);

  // Centré en haut. Quand l'ImpersonationBanner est affichée (sticky top-0,
  // ~32 px de hauteur, z-50), on pousse les toasts en dessous pour ne plus
  // qu'ils soient coupés. z-[60] pour rester au-dessus du backdrop blur du
  // header mobile éventuel.
  const impersonating = isImpersonating();

  return (
    <div
      className={cn(
        'fixed left-1/2 -translate-x-1/2 z-[60] space-y-2 w-full max-w-sm px-3 pointer-events-none',
        impersonating ? 'top-12' : 'top-3',
      )}
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cn(
            'pointer-events-auto rounded-lg border shadow-lg p-3 flex items-start gap-2 text-body animate-in slide-in-from-top-2',
            t.kind === 'success' && 'bg-emerald-50 border-emerald-200 text-emerald-800',
            t.kind === 'error' && 'bg-red-50 border-red-200 text-red-800',
            t.kind === 'info' && 'bg-white border-hair text-ink',
          )}
        >
          {t.kind === 'success' && <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />}
          {t.kind === 'error' && <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />}
          {t.kind === 'info' && <Info className="h-4 w-4 shrink-0 mt-0.5" />}
          <div className="flex-1 min-w-0 break-words">{t.message}</div>
          {t.action && (
            <button
              type="button"
              onClick={() => {
                t.action?.onClick();
                setToasts((prev) => prev.filter((x) => x.id !== t.id));
              }}
              className="shrink-0 text-meta font-medium underline underline-offset-2 hover:opacity-80"
            >
              {t.action.label}
            </button>
          )}
          <button
            type="button"
            onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
            className="shrink-0 text-ink-mute hover:text-ink-soft"
            aria-label="Fermer"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      ))}
    </div>
  );
}
